import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Instagram, Facebook, Twitter } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { CONTACT, policyPath } from "@/lib/policyMeta";
import { freeShippingLabel } from "@/lib/shipping";

const FOUNDER_SRC = `${process.env.PUBLIC_URL}/Founder.png`;

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const goToAboutSection = (sectionId) => (e) => {
    if (location.pathname !== "/about") return;

    e.preventDefault();
    const hash = `#${sectionId}`;
    const el = document.getElementById(sectionId);

    if (location.hash === hash) {
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    navigate(`/about${hash}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setLoading(true);
      await api.post("/newsletter", { email });
      toast.success("Welcome to Oori Label");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="mt-24 bg-muted/60 border-t border-border" data-testid="site-footer">
      {/* Stacked monogram lockup */}
      <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 pt-16 lg:pt-20 flex justify-center">
        <Link
          to="/"
          aria-label="Oori Label home"
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
              window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
              if (location.hash || location.search) navigate("/", { replace: true });
            }
          }}
        >
          <Logo variant="stacked" tagline />
        </Link>
      </div>

      {/* Newsletter */}
      <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 pt-16 lg:pt-20 pb-16 grid lg:grid-cols-2 gap-12">
        <div>
          <p className="overline mb-4">The Oori Letter</p>
          <h3 className="editorial-h2 max-w-md">
            Receive new <span className="italic">arrivals</span>, lookbooks & private invitations.
          </h3>
        </div>
        <form onSubmit={subscribe} className="self-end max-w-md w-full" data-testid="newsletter-form">
          <div className="flex items-center gap-4 border-b border-primary/30 pb-2">
            <input
              type="email"
              required
              data-testid="newsletter-email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent outline-none py-2 text-sm"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit"
              className="text-[11px] uppercase tracking-[0.22em] hover:opacity-70 transition min-h-11 px-4 shrink-0"
            >
              {loading ? "Subscribing…" : "Subscribe"}
            </button>
          </div>
          <p className="text-xs text-secondary mt-3">
            By subscribing you agree to our{" "}
            <Link to={policyPath("privacy")} className="link-underline">
              Privacy Policy
            </Link>
            .
          </p>
        </form>
      </div>

      <div className="border-t border-border bg-background/80">
        <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 py-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
          <img
            src={FOUNDER_SRC}
            alt="Payal, founder of Oori Label"
            className="w-16 h-16 rounded-full object-cover bg-muted shrink-0"
            data-testid="footer-founder-photo"
          />
          <p className="text-sm text-primary/80 leading-relaxed text-center sm:text-left max-w-xl">
            I'm Payal. I started OORI because I couldn't find clothes that felt like mine — not borrowed,
            not traditional, but actually mine.{" "}
            <Link to="/about" className="link-underline whitespace-nowrap">Read our story</Link>
          </p>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 text-sm">
          <div>
            <p className="overline mb-4">Shop</p>
            <ul className="space-y-3">
              <li><Link to="/category/ethnic" className="hover:opacity-70">Kurtis &amp; Ethnic</Link></li>
              <li><Link to="/category/coords" className="hover:opacity-70">Everyday Co-ords</Link></li>
              <li><Link to="/category/dresses" className="hover:opacity-70">Dresses</Link></li>
              <li><Link to="/category/tops" className="hover:opacity-70">Everyday Tops</Link></li>
            </ul>
          </div>
          <div>
            <p className="overline mb-4">Help</p>
            <ul className="space-y-3">
              <li><Link to="/about#size-guide" onClick={goToAboutSection("size-guide")} className="hover:opacity-70">Size Guide</Link></li>
              <li><Link to={policyPath("shipping")} className="hover:opacity-70">Shipping</Link></li>
              <li><Link to={policyPath("returns")} className="hover:opacity-70">Returns &amp; Exchanges</Link></li>
              <li><Link to={policyPath("contact")} className="hover:opacity-70">Contact</Link></li>
              <li>
                <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" className="hover:opacity-70">
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>
          <div>
            <p className="overline mb-4">Legal</p>
            <ul className="space-y-3">
              <li><Link to={policyPath("terms")} className="hover:opacity-70">Terms of Service</Link></li>
              <li><Link to={policyPath("privacy")} className="hover:opacity-70">Privacy Policy</Link></li>
              <li><Link to={policyPath()} className="hover:opacity-70">All Policies</Link></li>
            </ul>
          </div>
          <div>
            <p className="overline mb-4">Atelier</p>
            <ul className="space-y-3">
              <li><Link to="/about" className="hover:opacity-70">Our Story</Link></li>
              <li><Link to="/about" className="hover:opacity-70">Sustainability</Link></li>
              <li><Link to={policyPath("contact")} className="hover:opacity-70">Press</Link></li>
              <li><Link to={policyPath("contact")} className="hover:opacity-70">Careers</Link></li>
            </ul>
          </div>
          <div>
            <p className="overline mb-4">Follow</p>
            <ul className="space-y-3">
              <li><a className="inline-flex items-center gap-2 hover:opacity-70" href="https://instagram.com" target="_blank" rel="noopener noreferrer"><Instagram className="w-4 h-4" /> Instagram</a></li>
              <li><a className="inline-flex items-center gap-2 hover:opacity-70" href="https://facebook.com" target="_blank" rel="noopener noreferrer"><Facebook className="w-4 h-4" /> Facebook</a></li>
              <li><a className="inline-flex items-center gap-2 hover:opacity-70" href="https://twitter.com" target="_blank" rel="noopener noreferrer"><Twitter className="w-4 h-4" /> Twitter</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="max-w-[1480px] mx-auto px-4 sm:px-8 lg:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-secondary">
          <p>© {new Date().getFullYear()} Oori Label. Crafted in India. · {freeShippingLabel}</p>
          <p className="serif italic">Refined by Simplicity.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
